import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { prop } from 'rambdax';
import styled from 'styled-components';
import {
  birthdayIcon,
  BoardMemberImage,
  buildingIcon,
  concernsIcon,
  emailIcon,
  employeeIdIcon,
  facebookIcon,
  homeIcon,
  industryIcon,
  inIcon,
  instagramIcon,
  jobIcon,
  jobPositionIcon,
  OpenBookIcon,
  phoneIcon,
  phoneNumberIcon,
  prohibitionIcon,
  redPhoneNumber,
  tiktokIcon,
  twitterIcon,
  webIcon,
  winningIcon,
  youtubeIcon,
} from '@/assets/images';
import AttributeIcon, { AttributeIconType } from '@/components/AttributeIcon';
import useMobileView from '@/hooks/ui/useMobileView';

const StyledEditIcon = styled.img`
  height: ${prop('size')};
  width: ${prop('size')};
`;

function EditDonorIcon({ src, ...rest }) {
  const isMobile = useMobileView();

  const size = useMemo(() => (isMobile ? '16px' : '24px'), [isMobile]);

  return <StyledEditIcon size={size} src={src} {...rest} />;
}

EditDonorIcon.propTypes = {
  src: PropTypes.string.isRequired,
};

const StyledIcon = styled.img`
  height: ${({ social }) => (social ? 24 : 16)}px;
  margin-top: ${({ summary }) => (summary ? 0 : 2)}px;
  width: ${({ social }) => (social ? 24 : 16)}px;
`;

const BirthdayIcon = (
  <StyledIcon
    alt="birthday-icon"
    data-testid="birthday-icon"
    src={birthdayIcon}
  />
);
const BuildingIcon = (
  <StyledIcon
    alt="building-icon"
    data-testid="building-icon"
    src={buildingIcon}
  />
);
const EditBuildingIcon = (
  <EditDonorIcon
    alt="building-icon"
    data-testid="building-icon"
    src={buildingIcon}
  />
);
const FacebookIcon = (
  <StyledIcon
    alt="Facebook icon"
    data-testid="facebook-icon"
    src={facebookIcon}
    social="true"
  />
);
const HomeIcon = (
  <StyledIcon alt="home-icon" data-testid="home-icon" src={homeIcon} />
);
const LinkedInIcon = (
  <StyledIcon
    alt="LinkedIn icon"
    data-testid="linked-in-icon"
    src={inIcon}
    social="true"
  />
);
const InstagramIcon = (
  <StyledIcon
    alt="Instagram icon"
    data-testid="instagram-icon"
    src={instagramIcon}
    social="true"
  />
);
const TwitterIcon = (
  <StyledIcon
    alt="Twitter icon"
    data-testid="twitter-icon"
    src={twitterIcon}
    social="true"
  />
);
const TiktokIcon = (
  <StyledIcon
    alt="TikTok icon"
    data-testid="tiktok-icon"
    src={tiktokIcon}
    social="true"
  />
);
const YouTubeIcon = (
  <StyledIcon
    alt="Youtube icon"
    data-testid="youtube-icon"
    src={youtubeIcon}
    social="true"
  />
);
const WebIcon = (
  <StyledIcon alt="web-icon" data-testid="web-icon" src={webIcon} />
);
const EditWebIcon = (
  <EditDonorIcon alt="web-icon" data-testid="web-icon" src={webIcon} />
);
const EmailIcon = (
  <StyledIcon alt="email-icon" data-testid="email-icon" src={emailIcon} />
);
const JobIcon = (
  <StyledIcon alt="job-icon" data-testid="job-icon" src={jobIcon} />
);
const JobPositionIcon = (
  <StyledIcon
    alt="job-position-icon"
    data-testid="job-position-icon"
    src={jobPositionIcon}
  />
);
const PhoneIcon = (
  <StyledIcon alt="phone-icon" data-testid="phone-icon" src={phoneNumberIcon} />
);
const RedPhoneIcon = (
  <StyledIcon
    alt="red-phone-icon"
    data-testid="red-phone-icon"
    src={redPhoneNumber}
  />
);
const WorkPhoneIcon = (
  <StyledIcon
    alt="work-phone-icon"
    data-testid="work-phone-icon"
    src={phoneIcon}
  />
);
const EditWorkPhoneIcon = (
  <EditDonorIcon
    alt="work-phone-icon"
    data-testid="work-phone-icon"
    src={phoneIcon}
  />
);
const WinningIcon = (
  <StyledIcon alt="winning-icon" data-testid="winning-icon" src={winningIcon} />
);
const ConcernsIcon = (
  <StyledIcon
    alt="concerns-icon"
    data-testid="concerns-icon"
    src={concernsIcon}
  />
);
const ProhibitionsIcon = (
  <StyledIcon
    alt="prohibitions-icon"
    data-testid="prohibitions-icon"
    src={prohibitionIcon}
  />
);
const IndustryIcon = (
  <StyledIcon
    alt="industry-icon"
    data-testid="industry-icon"
    src={industryIcon}
  />
);
const EmployeeIdIcon = (
  <StyledIcon
    alt="employee-id-icon"
    data-testid="employee-id-icon"
    src={employeeIdIcon}
  />
);
const EditEmployeeIdIcon = (
  <EditDonorIcon
    alt="employee-id-icon"
    data-testid="employee-id-icon"
    src={employeeIdIcon}
  />
);

const BoardMemberIcon = (
  <AttributeIcon
    data-testid="board-member-icon"
    src={BoardMemberImage}
    type="boardMember"
  />
);

const MajorDonorIcon = <AttributeIcon type={AttributeIconType.MAJOR_DONOR} />;
const RecurringDonorIcon = <AttributeIcon type={AttributeIconType.RECURRING} />;
const VolunteerIcon = <AttributeIcon type={AttributeIconType.VOLUNTEER} />;
const MemberIcon = <AttributeIcon type={AttributeIconType.MEMBER} />;
const BioIcon = (
  <StyledIcon data-testid="bio-icon" id="bio-icon" src={OpenBookIcon} />
);

const AttributeAdvocateIcon = (
  <AttributeIcon type={AttributeIconType.ADVOCATE} />
);
const AttributeCorporationIcon = (
  <AttributeIcon type={AttributeIconType.CORPORATION} />
);
const AttributeDonorIcon = <AttributeIcon type={AttributeIconType.DONOR} />;
const AttributeFoundationIcon = (
  <AttributeIcon type={AttributeIconType.FOUNDATION} />
);
const AttributeGovernmentIcon = (
  <AttributeIcon type={AttributeIconType.GOVERNMENT} />
);
const AttributeMajorDonorIcon = (
  <AttributeIcon type={AttributeIconType.MAJOR_DONOR} />
);
const AttributePartnerIcon = <AttributeIcon type={AttributeIconType.PARTNER} />;
const AttributeRecipientIcon = (
  <AttributeIcon type={AttributeIconType.RECIPIENT} />
);
const AttributeRecurringIcon = (
  <AttributeIcon type={AttributeIconType.RECURRING} />
);
const AttributeVipIcon = <AttributeIcon type={AttributeIconType.VIP} />;

export {
  AttributeAdvocateIcon,
  AttributeCorporationIcon,
  AttributeDonorIcon,
  AttributeFoundationIcon,
  AttributeGovernmentIcon,
  AttributeMajorDonorIcon,
  AttributePartnerIcon,
  AttributeRecipientIcon,
  AttributeRecurringIcon,
  AttributeVipIcon,
  BioIcon,
  BirthdayIcon,
  BuildingIcon,
  EditBuildingIcon,
  EmailIcon,
  EmployeeIdIcon,
  EditEmployeeIdIcon,
  FacebookIcon,
  JobIcon,
  JobPositionIcon,
  HomeIcon,
  RedPhoneIcon,
  InstagramIcon,
  LinkedInIcon,
  PhoneIcon,
  TwitterIcon,
  TiktokIcon,
  YouTubeIcon,
  WebIcon,
  EditWebIcon,
  WorkPhoneIcon,
  EditWorkPhoneIcon,
  WinningIcon,
  ConcernsIcon,
  ProhibitionsIcon,
  IndustryIcon,
  EditDonorIcon,
  BoardMemberIcon,
  MajorDonorIcon,
  RecurringDonorIcon,
  VolunteerIcon,
  MemberIcon,
};
